import { IErrorMessage } from '@shared/hooks/use-error-message';
import {
  DevicesRowDataProps,
  GroupedDevicesModel
} from '@modules/digital-signage/model/devices.model';
import {
  DeviceGroupDeviceModel,
  DeviceGroupModel,
  DeviceGroupReqModel
} from '@models/digital-signage/device-groups.model.ts';
import { DeviceGroupScheduleModel } from '@models/digital-signage/device-group-schedules.model.ts';
import { mapSchedulesToReq } from '@modules/digital-signage/utils/schedule-form.utils.ts';
import { PlaylistScreenOption } from '@modules/digital-signage/model/playlists.model.ts';
import { MediaTypeEnum } from '@modules/digital-signage/model/screens.model.ts';
import {
  DeviceStatusFilterEnum,
  GroupSettingsFormValues,
  StatusLozengeToneEnum
} from '@modules/digital-signage/model/group-settings.model.ts';

export const playlistErrorMessages: IErrorMessage = {
  name: {
    required: 'validation_name_required',
    notOnlySpaces: 'validation_notOnly_spaces_required'
  }
};

export const deviceGroupErrorMessages: IErrorMessage = {
  name: {
    required: 'validation_name_required',
    notOnlySpaces: 'validation_notOnly_spaces_required'
  }
};

export const screensErrorMessages: IErrorMessage = {
  title: {
    required: 'validation_title_required',
    notOnlySpaces: 'validation_notOnly_spaces_required'
  },
  mediaUrl: {
    pattern: 'validation_url_pattern'
  }
};

export const digitalSignageCodeErrorMessages: IErrorMessage = {
  code: {
    required: 'validation_code_required',
    notOnlySpaces: 'validation_notOnly_spaces_required'
  }
};

export const moveToGroupErrorMessages: IErrorMessage = {
  deviceGroup: {
    required: 'validation_device_group_required'
  }
};

// A group carrying the defaultDevice key is a technical one, auto-created for
// a single device, so such a device is not assigned to any real group.
export const isRealDeviceGroup = (group?: DeviceGroupModel | null): boolean =>
  !!group?.name && group.defaultDevice === undefined;

export const isDefaultDeviceGroup = (
  group?: DeviceGroupModel | null
): boolean => !group?.name && !!group?.defaultDevice;

export const countAddedDevices = (
  devices: PlaylistScreenOption[] = [],
  initialDevices: (Partial<PlaylistScreenOption> | undefined)[] = []
): number => {
  const initialIds = new Set(initialDevices.map((device) => device?.id));
  return devices.filter((device) => !initialIds.has(device.id)).length;
};

const isInRealDeviceGroup = (device: DevicesRowDataProps): boolean =>
  isRealDeviceGroup(device.deviceGroup);

export const getDeviceKey = (device: DevicesRowDataProps): string =>
  (device.deviceId ?? device._id ?? device.name) as string;

export const groupDevicesByCurrentGroup = (
  devices: DevicesRowDataProps[]
): GroupedDevicesModel[] => {
  const groups = new Map<string, GroupedDevicesModel>();

  devices.filter(isInRealDeviceGroup).forEach((device) => {
    const groupId = device.deviceGroup?._id as string;
    const group = groups.get(groupId);

    if (group) {
      group.devices.push(device);
      return;
    }

    groups.set(groupId, {
      groupId,
      groupName: device.deviceGroup?.name as string,
      devices: [device]
    });
  });

  return Array.from(groups.values());
};

// Turns one group's local selection into the next overall list of devices to
// move. Rebuilding it by filtering the source devices keeps their order, the
// other groups' devices and the devices that isInRealDeviceGroup filters out
// (they are never shown in the alert but always belong to the payload).
export const applyGroupDevicesSelection = (
  sourceDevices: DevicesRowDataProps[],
  selectedDevices: DevicesRowDataProps[],
  groupDevices: DevicesRowDataProps[],
  nextGroupDevices: DevicesRowDataProps[]
): DevicesRowDataProps[] => {
  const keys = new Set(selectedDevices.map(getDeviceKey));

  groupDevices.forEach((device) => keys.delete(getDeviceKey(device)));
  nextGroupDevices.forEach((device) => keys.add(getDeviceKey(device)));

  return sourceDevices.filter((device) => keys.has(getDeviceKey(device)));
};

export const eventErrorMessages: IErrorMessage = {
  name: {
    required: 'validation_name_required',
    notOnlySpaces: 'validation_notOnly_spaces_required'
  },
  location: {
    required: 'validation_location_required',
    notOnlySpaces: 'validation_notOnly_spaces_required'
  },
  startDateTime: {
    required: 'validation_start_date_required'
  },
  categories: {
    required: 'validation_categories_required'
  }
};

export const mediaTypeFilterData = [
  {
    title: 'Image',
    value: MediaTypeEnum.Image
  },
  {
    title: 'Video',
    value: MediaTypeEnum.Video
  },
  {
    title: 'Html',
    value: MediaTypeEnum.Html
  }
];

export const digitalSignageScreensTabsData = [
  { label: 'tab_label_file', value: 'file' },
  { label: 'tab_label_url', value: 'url' },
  { label: 'tab_label_app_path', value: 'appPath' }
];

export const toDeviceOptions = (
  devices?: DeviceGroupDeviceModel[]
): PlaylistScreenOption[] =>
  (devices ?? [])
    // Table rows may carry bare ids; full data comes from GET /device-groups/{id}.
    .filter((device) => typeof device === 'object' && !!device?._id)
    .map((device) => ({
      id: device._id,
      title: device.name,
      value: device.name
    }));

const getRefId = (ref: unknown): string | undefined =>
  typeof ref === 'string' ? ref : (ref as { _id?: string } | null)?._id;

// The group may send its devices as objects or bare ids, so they are resolved
// against the full devices list, which also knows each device's group.
export const getGroupDevices = (
  groupId: string,
  groupDevices: (DeviceGroupDeviceModel | string)[] = [],
  devicesList: DeviceGroupDeviceModel[] = []
): DeviceGroupDeviceModel[] => {
  const byId = new Map(devicesList.map((device) => [device._id, device]));
  const result = new Map<string, DeviceGroupDeviceModel>();

  groupDevices.forEach((ref) => {
    const id = getRefId(ref);
    const device =
      (id && byId.get(id)) || (typeof ref === 'object' ? ref : undefined);
    if (id && device) result.set(id, device);
  });
  devicesList
    .filter((device) => getRefId(device.deviceGroup) === groupId)
    .forEach((device) => result.set(device._id, device));

  return Array.from(result.values());
};

export const getDevicesFromOtherGroups = (
  selectedDevices: PlaylistScreenOption[],
  devicesList: DeviceGroupDeviceModel[],
  deviceGroups: DeviceGroupModel[],
  currentGroupId?: string
): DevicesRowDataProps[] => {
  const devicesById = new Map(
    devicesList.map((device) => [device._id, device])
  );
  const groupsById = new Map(
    deviceGroups
      .filter((group) => isRealDeviceGroup(group))
      .map((group) => [group._id, group])
  );

  return selectedDevices.flatMap((option) => {
    const device = devicesById.get(option.id);
    const groupId = getRefId(device?.deviceGroup);
    const group =
      groupId && groupId !== currentGroupId
        ? groupsById.get(groupId)
        : undefined;

    return device && group
      ? [{ ...device, deviceGroup: group } as unknown as DevicesRowDataProps]
      : [];
  });
};

export const applyMoveSelection = (
  formDevices: PlaylistScreenOption[],
  sourceDevices: DevicesRowDataProps[],
  nextSelected: DevicesRowDataProps[]
): PlaylistScreenOption[] => {
  const sourceIds = new Set(sourceDevices.map((device) => device._id));
  const nextIds = new Set(nextSelected.map((device) => device._id));
  const kept = formDevices.filter(
    (option) => !sourceIds.has(option.id) || nextIds.has(option.id)
  );
  const keptIds = new Set(kept.map((option) => option.id));
  const restored = nextSelected
    .filter((device) => device._id && !keptIds.has(device._id))
    .map((device) => ({
      id: device._id as string,
      title: device.name,
      value: device.name
    }));

  return [...kept, ...restored];
};

export const DEVICE_STATUS_FILTERS = [
  { value: DeviceStatusFilterEnum.All, tone: StatusLozengeToneEnum.Neutral },
  { value: DeviceStatusFilterEnum.Active, tone: StatusLozengeToneEnum.Success },
  { value: DeviceStatusFilterEnum.Inactive, tone: StatusLozengeToneEnum.Danger }
];

export const STATUS_LOZENGE_TONES: Record<StatusLozengeToneEnum, string> = {
  [StatusLozengeToneEnum.Success]: 'bg-(--green-alpha-200)! text-(--teal-2)!',
  [StatusLozengeToneEnum.Brand]: 'bg-(--primary-100)! text-(--primary)!',
  [StatusLozengeToneEnum.BrandSoft]: 'bg-(--primary-100)! text-(--primary)!',
  [StatusLozengeToneEnum.Warning]: 'bg-(--yellow)/15! text-(--yellow)!',
  [StatusLozengeToneEnum.Neutral]: 'bg-(--gray-10)! text-(--gray-2)!',
  [StatusLozengeToneEnum.Danger]: 'bg-(--red-4)/10! text-(--red-4)!',
  [StatusLozengeToneEnum.Info]: 'bg-(--blue-alpha-100)! text-(--blue)!'
};

export const DEVICE_STATUS_TONES: Record<
  Exclude<DeviceStatusFilterEnum, DeviceStatusFilterEnum.All>,
  StatusLozengeToneEnum
> = {
  [DeviceStatusFilterEnum.Active]: StatusLozengeToneEnum.Success,
  [DeviceStatusFilterEnum.Inactive]: StatusLozengeToneEnum.Danger
};

export const getDeviceStatus = (device?: DeviceGroupDeviceModel) =>
  device?.isActive
    ? DeviceStatusFilterEnum.Active
    : DeviceStatusFilterEnum.Inactive;

export const buildGroupSettingsReq = (
  data: GroupSettingsFormValues,
  schedules: DeviceGroupScheduleModel[],
  isCreate: boolean,
  savedGroup?: DeviceGroupModel | null
): DeviceGroupReqModel => {
  const name = data.groupName.trim();
  const saved = isCreate ? null : savedGroup;
  const screenId = data.assignedScreen?.id;
  const playlistId = data.assignedPlaylist?.id;

  return {
    ...(name && { name }),
    ...((screenId || saved?.defaultScreen) && {
      defaultScreen: screenId || null
    }),
    ...((playlistId || saved?.defaultPlaylist) && {
      defaultPlaylist: playlistId || null
    }),
    devices: data.devices.map((device) => device.id),
    ...((schedules.length || saved?.playlistSchedules?.length) && {
      playlistSchedules: mapSchedulesToReq(schedules, !isCreate)
    })
  };
};
